import * as React from "react"
import { MoreHorizontal, Receipt, Trash2, Edit, WifiOff, Repeat } from "lucide-react"
import { Card, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatCurrency } from "@/lib/utils"
import type { Expense } from "@/types/domain.types"

const formatFullDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'Unknown date'
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return 'Invalid date'

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date)
}

interface ExpenseCardProps {
  expense: Expense
  onEdit?: (expense: Expense) => void
  onDelete?: (expenseId: string) => void
  showCategory?: boolean
  showPaidBy?: boolean
}

export const ExpenseCard: React.FC<ExpenseCardProps> = ({
  expense,
  onEdit,
  onDelete,
  showCategory = true,
  showPaidBy = true,
}) => {
  // Backend returns 'user' for the person who paid
  const paidByUser = expense.paid_by_user || expense.user

  // Check if this is an offline expense
  const isOffline = (expense as any).is_offline || (expense as any)._local?.localOnly;

  // Expenses generated from a recurring rule (subscriptions etc.)
  const isRecurring = !!(expense as any).recurring_expense_id;

  return (
    <Card className={`relative bg-white border-2 ${isOffline ? 'border-amber-200' : 'border-gray-100'} rounded-xl shadow-sm`}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-start sm:items-center justify-between gap-3">
          {/* Left side: Icon and details */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {/* Simple, clean icon */}
            <div className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-gray-50 flex-shrink-0">
              {expense.category?.emoji ? (
                <span className="text-lg sm:text-xl">
                  {expense.category.emoji}
                </span>
              ) : (
                <Receipt className="h-5 w-5 text-gray-400" />
              )}
            </div>
            {/* Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mt-1">
                {isOffline && (
                  <Badge variant="outline" className="gap-1 text-xs bg-amber-50 border-amber-200 text-amber-700">
                    <WifiOff className="h-3 w-3" />
                    Pending sync
                  </Badge>
                )}
                {isRecurring && (
                  <Badge variant="outline" className="gap-1 text-xs bg-blue-50 border-blue-200 text-blue-700">
                    <Repeat className="h-3 w-3" />
                    Recurring
                  </Badge>
                )}
                {showCategory && expense.category && (
                  <span className="text-xs text-gray-600 font-medium">
                    {expense.category.name}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {formatFullDate(expense.date || expense.created_at)}
                </span>
              </div>
              <h3 className="font-semibold text-sm sm:text-base text-gray-900 line-clamp-1">
                {expense.description || 'Untitled expense'}
              </h3>
            </div>
          </div>
          {/* Right side: Amount and actions */}
          <div className="flex items-start gap-2">
            <div className="text-right">
              {showPaidBy && (
                <div className="text-xs text-gray-500">
                  {paidByUser?.name || expense.user?.name || 'Unknown'}
                </div>
              )}
              <div className="font-semibold text-base sm:text-lg text-gray-900">
                {formatCurrency(expense.amount, expense.currency || expense.currency_code || 'EUR')}
              </div>
            </div>
            {(onEdit || onDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl border-0 shadow-xl">
                  {onEdit && (
                    <DropdownMenuItem onClick={() => onEdit(expense)} className="rounded-lg">
                      <Edit className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem
                      onClick={() => onDelete(expense.id)}
                      className="text-destructive rounded-lg"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>
    </Card>
  )
}